<template>
  <rpm-layout>
    <div class="flex items-center bg-white rounded-lg p-2 mb-2 space-x-2">
      <div class="flex-1 flex justify-center">
        <v-autocomplete
            v-model="client"
            :items="availableClients"
            item-text="name"
            item-value="id"
            label="Client"
            outlined
            hide-details
            persistent-placeholder
            style="width: 250px;"
        ></v-autocomplete>
      </div>
      <div class="flex-1 flex justify-center">
        <v-select
            v-model="category"
            :items="categoryOptions"
            item-text="text"
            item-value="value"
            label="Category"
            persistent-placeholder
            outlined
            hide-details
            clearable
            style="width: 200px;"
        ></v-select>
      </div>
      <div class="flex-1 flex justify-center">
        <v-menu v-model="startMenu" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" small color="primary">
              {{ dateFrom | dateNonLocal }}
            </v-btn>
          </template>
          <v-date-picker v-model="dateFrom" @input="startMenu = false"/>
        </v-menu>
      </div>
      <div class="flex-1 flex justify-center">
        <v-menu v-model="endMenu" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" small color="primary">
              {{ dateTo | dateNonLocal }}
            </v-btn>
          </template>
          <v-date-picker v-model="dateTo" @input="endMenu = false"/>
        </v-menu>
      </div>
      <div class="flex-1 flex justify-center">
        <el-button type="info" @click="refreshData">Refresh</el-button>
      </div>
    </div>

    <div class="bg-white rounded-lg p-2">
      <v-tabs v-model="activeTab">
        <v-tab>A/R Report</v-tab>
        <v-tab>Amounts Chart</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- A/R Report Tab -->
        <v-tab-item>
          <div class="p-2">
            <div class="flex flex-wrap items-center p-2 gap-2">
              <v-text-field
                  class="max-w-sm"
                  placeholder="Search"
                  v-model="search"
                  outlined
                  hide-details
              ></v-text-field>
              <v-select
                  class="max-w-sm"
                  v-model="selectedStatuses"
                  :items="availableStatuses"
                  label="Status"
                  multiple
                  outlined
                  hide-details
                  persistent-placeholder
              ></v-select>
              <v-autocomplete
                  class="max-w-sm"
                  v-model="selectedInsuranceNames"
                  :items="availableInsuranceNames"
                  label="Insurance"
                  multiple
                  outlined
                  hide-details
                  clearable
                  persistent-placeholder
              ></v-autocomplete>
              <v-autocomplete
                  class="max-w-sm mr-2"
                  v-model="selectedBillers"
                  :items="availableBillers"
                  label="Billers"
                  multiple
                  outlined
                  hide-details
                  clearable
                  persistent-placeholder
              ></v-autocomplete>
              <v-checkbox
                  v-model="hideEmptyEncounterIds"
                  label="Hide empty Encounter IDs"
                  hide-details
                  class="mt-0 pt-0 shrink"
              ></v-checkbox>

              <a
                  target="_blank"
                  :download="`ar-report-${dateFrom}-${dateTo}.csv`"
                  :href="csvLink()"
                  class="ml-auto py-2.5 px-5 text-sm font-medium text-gray-700 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-200 whitespace-nowrap"
                  v-if="tableDataFiltered.length"
              >
                Download Report CSV
              </a>
            </div>
            <div class="flex justify-center items-center p-2 mb-2">
              <div class="mx-4">
                <span class="font-semibold">Avg Expected:</span> ${{ avgExpectedReimbursement }}
              </div>
              <div class="mx-4">
                <span class="font-semibold">Avg Billed:</span> ${{ avgBilledAmount }}
              </div>
              <div class="mx-4">
                <span class="font-semibold">Avg Received:</span> ${{ avgReceivedAmount }}
              </div>
              <div class="mx-4">
                <span class="font-semibold">Avg A/R Aging:</span> {{ avgArAging }} days
              </div>
            </div>

            <div class="flex justify-center items-center p-2 mb-4">
              <div class="mx-4">
                <span class="font-semibold">Total Expected:</span> ${{ totalExpectedReimbursement }}
              </div>
              <div class="mx-4">
                <span class="font-semibold">Total Billed:</span> ${{ totalBilledAmount }}
              </div>
              <div class="mx-4">
                <span class="font-semibold">Total Received:</span> ${{ totalReceivedAmount }}
              </div>
              <div class="mx-4">
                <span class="font-semibold">Total Outstanding:</span> {{ totalOutstanding }}
              </div>
            </div>

            <div class="flex justify-start mb-2 space-x-2">
              AR Report Table

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
                </template>
                <span>
                    Show's claims data grouped by patient, encounter, CPT code, and service date. Click the expand icon on each row to see individual transactions that make up that claim group.<br/>
                    * 'N/A' in insurance filter means the claim has a blank insurance name.

                  </span>
              </v-tooltip>
            </div>

            <div class="flex justify-end mb-2 space-x-2">
              <v-btn small outlined @click="expanded = groupedTableData.map(r => r)">Expand All</v-btn>
              <v-btn small outlined @click="expanded = []">Collapse All</v-btn>
            </div>

            <v-data-table
                :loading="loading"
                :items="groupedTableData"
                :headers="headers"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                class="elevation-1"
                :fixed-header="true"
                height="600px"
                show-expand
                :expanded.sync="expanded"
                item-key="groupKey"
            >
              <template v-slot:[`item.patient_name`]="{ item }">
                <v-menu bottom open-on-hover>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip small v-bind="attrs" v-on="on">
                      {{ item.patient_name }}
                    </v-chip>
                  </template>
                  <v-card>
                    <v-card-text>
                      <p>Name: {{ item.patient_name }}</p>
                      <p>Patient ID: {{ item.patient_id }}</p>
                      <v-btn text small @click="goToPatient(item.patient_id, true)">Details</v-btn>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </template>

              <template v-slot:[`item.service_date`]="{ item }">
                {{ dateFormatter(item.service_date) }}
              </template>

              <template v-slot:[`item.posting_date`]="{ item }">
                {{ dateFormatter(item.posting_date) }}
              </template>

              <template v-slot:[`item.expected_reimbursement`]="{ item }">
                {{ item.expected_reimbursement != null ? '$' + formatCurrency(item.expected_reimbursement) : 'N/A' }}
              </template>

              <template v-slot:[`item.billed_amount`]="{ item }">
                {{ item.billed_amount != null ? '$' + formatCurrency(item.billed_amount) : 'N/A' }}
              </template>

              <template v-slot:[`item.total_received`]="{ item }">
                {{ item.total_received != null ? '$' + formatCurrency(item.total_received) : 'N/A' }}
              </template>

              <template v-slot:[`item.ar_aging`]="{ item }">
                {{ item.ar_aging !== null ? item.ar_aging + ' days' : 'N/A' }}
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0">
                  <v-simple-table dense class="ml-8 mr-4 my-2">
                    <template v-slot:default>
                      <thead>
                      <tr>
                        <th>Biller Transaction ID</th>
                        <th>Insurance</th>
                        <th>Payment Posted Date</th>
                        <th>Received Amount</th>
                        <th>Total Balance</th>
                        <th>Status</th>
                        <th>Comment</th>
                        <th>Imported At</th>
                        <th>Import ID</th>
                        <th>Latest Update Import ID</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="subRow in item.transactions" :key="subRow.unique_key">
                        <td>{{ subRow.biller_transaction_id }}</td>
                        <td>{{ subRow.insurance_name }}</td>
                        <td>{{ dateFormatter(subRow.posted_date) }}</td>
                        <td>{{ subRow.received_amount != null ? '$' + formatCurrency(subRow.received_amount) : 'N/A' }}</td>
                        <td>{{ subRow.total_balance != null ? '$' + formatCurrency(subRow.total_balance) : 'N/A' }}</td>
                        <td>{{ subRow.status }}</td>
                        <td>{{ subRow.comment || '' }}</td>
                        <td>{{ dateFormatter(subRow.updated_at) }}</td>
                        <td>{{ subRow.import_id || '' }}</td>
                        <td>{{ subRow.latest_update_import_id || '' }}</td>
                      </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </td>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>

        <!-- A/R Chart Tab -->
        <v-tab-item>
          <div class="p-4">
            <div class="flex items-center mb-4">
              <span class="mr-2">Time Period:</span>
              <select v-model="chartTimePeriod" class="border rounded px-3 py-1.5 text-sm pr-8">
                <option value="daily">Daily</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div v-if="chartLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="chartData" class="chart-container">
              <span class="pb-2">
                Amount Averages Over Time

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
                  </template>
                  <span>
                    Shows the average expected reimbursement, billed amount, and received amount over time.<br/>
                    Expected Reimbursement: The average amount expected to be reimbursed for claims posted in that period.<br/>
                    Billed Amount: The average amount billed for claims posted in that period.<br/>
                    Received Amount: The average amount received for claims posted in that period.
                  </span>
                </v-tooltip>
              </span>

              <chart :data="chartData" :options="chartOptions"/>
            </div>
            <div v-else class="text-center py-8 text-gray-500">
              No data available for the selected period
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </rpm-layout>
</template>

<script>
import RpmLayout from '../../layouts/RpmLayout.vue';
import Chart from '../../components/Chart.vue';
import moment from 'moment-timezone';
import PatientMixin from '@/mixins/Patient';

export default {
  mixins: [PatientMixin],
  components: { RpmLayout, Chart },
  data() {
    return {
      activeTab: 0,
      search: '',
      selectedStatuses: [],
      selectedInsuranceNames: [],
      selectedBillers: [],
      hideEmptyEncounterIds: false,
      category: null,
      categoryOptions: [
        { text: 'RPM', value: 'rpm' },
        { text: 'CCM', value: 'ccm' },
        { text: 'APCM', value: 'apcm' },
      ],
      client: null,
      dateFrom: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      dateTo: moment().endOf('month').format('YYYY-MM-DD'),
      startMenu: false,
      endMenu: false,
      tableData: [],
      token: null,
      loading: true,
      sortBy: 'encounter_id',
      sortDesc: false,
      expanded: [],
      // Chart specific data
      chartTimePeriod: 'daily',
      chartLoading: false,
      arChartData: null,
      chartDataStale: true,
    };
  },
  created: async function () {
    this.token = await this.$auth.getTokenSilently();
    Promise.all([
      !this.$store.state.patients.authUser.id
          ? this.$store.dispatch('getAuthUser')
          : null,
    ]).finally(() => {
      if (this.availableClients && this.availableClients.length > 0) {
        this.client = this.availableClients[0].id;
      }
      this.loading = false;
    });
  },
  computed: {
    tableDataFiltered() {
      if (!this.tableData || this.tableData.length === 0) return [];

      let filtered = this.tableData;

      // Filter by category
      if (this.category) {
        filtered = filtered.filter(r => r.category === this.category);
      }

      // Filter by insurance name (only when user has selected specific names)
      if (this.selectedInsuranceNames.length > 0) {
        filtered = filtered.filter(r => this.selectedInsuranceNames.includes(r.insurance_name));
      }

      // Filter by biller
      if (this.selectedBillers.length > 0) {
        filtered = filtered.filter(r => this.selectedBillers.includes(r.biller));
      }

      // Filter by search
      if (this.search === '') {
        return filtered;
      }
      return filtered.filter(
          (r) =>
              Object.values(r).filter((val) =>
                  String(val).toLowerCase().includes(this.search.toLowerCase())
              ).length
      );
    },
    availableClients() {
      const authUser = this.$store.state.patients.authUser;
      return (authUser && authUser.available_clients) || [];
    },
    availableStatuses() {
      if (!this.tableData || this.tableData.length === 0) return [];
      const statuses = [...new Set(this.tableData.map(row => row.status).filter(Boolean))].sort();
      return statuses;
    },
    availableInsuranceNames() {
      if (!this.tableData || this.tableData.length === 0) return [];
      const names = [...new Set(this.tableData.filter(row => row.insurance_name).map(row => row.insurance_name))].sort();
      return names;
    },
    availableBillers() {
      if (!this.tableData || this.tableData.length === 0) return [];
      const billers = [...new Set(this.tableData.map(row => row.biller))].sort();
      return billers;
    },
    headers() {
      return [
        { text: '', value: 'data-table-expand', sortable: false },
        { text: 'Name', value: 'patient_name', sortable: true, width: '200px' },
        { text: 'Client', value: 'client_name', sortable: true, width: '150px' },
        { text: 'Encounter ID', value: 'encounter_id', sortable: true, width: '120px' },
        { text: 'Category', value: 'category', sortable: true, width: '120px' },
        { text: 'CPT Code', value: 'cpt_code', sortable: true, width: '100px' },
        { text: 'Service Date', value: 'service_date', sortable: true, width: '150px' },
        { text: 'Biller', value: 'biller', sortable: true, width: '100px' },
        { text: 'Claim Posting Date', value: 'posting_date', sortable: true, width: '130px' },
        { text: 'Expected Reimbursement', value: 'expected_reimbursement', sortable: true, width: '180px' },
        { text: 'Billed Amount', value: 'billed_amount', sortable: true, width: '130px' },
        { text: 'Total Received', value: 'total_received', sortable: true, width: '140px' },
        { text: 'A/R Aging', value: 'ar_aging', sortable: true, width: '100px' },
        { text: 'Latest Status', value: 'latest_status', sortable: true, width: '130px' },
      ];
    },
    groupedTableData() {
      if (!this.tableDataFiltered || this.tableDataFiltered.length === 0) return [];

      const groups = {};

      this.tableDataFiltered.forEach((row) => {
        const key = row.patient_id + '|' + row.cpt_code + '|' + row.encounter_id + '|' + row.service_date;

        if (!groups[key]) {
          groups[key] = {
            groupKey: key,
            patient_id: row.patient_id,
            patient_name: row.patient_name,
            client_name: row.client_name || '',
            encounter_id: row.encounter_id,
            category: row.category,
            cpt_code: row.cpt_code,
            insurance_names: new Set(),
            service_date: row.service_date,
            biller: row.biller,
            posting_date: row.posting_date,
            expected_reimbursement: null,
            latest_posted_date: null,
            latest_status: null,
            billed_amount: row.billed_amount,
            total_received: 0,
            ar_aging: null,
            transactions: [],
            posted_dates: []
          };
        }

        // Collect unique insurance names
        if (row.insurance_name) {
          groups[key].insurance_names.add(row.insurance_name);
        }

        // Track the latest expected_reimbursement and latest_status (from row with max posted_date)
        if (row.posted_date && (!groups[key].latest_posted_date || moment(row.posted_date).isAfter(moment(groups[key].latest_posted_date)))) {
          groups[key].latest_posted_date = row.posted_date;
          groups[key].expected_reimbursement = row.expected_reimbursement;
          groups[key].latest_status = row.status;
        }
        // Fallback for rows without posted_date (e.g., unbilled claims)
        if (!groups[key].latest_status && row.status) {
          groups[key].latest_status = row.status;
          groups[key].expected_reimbursement = row.expected_reimbursement;
        }

        // Sum received amounts
        if (row.received_amount != null) {
          groups[key].total_received += parseFloat(row.received_amount);
        }

        // Collect posted dates for A/R aging calculation
        if (row.posted_date) {
          groups[key].posted_dates.push(row.posted_date);
        }

        // Add transaction to sub-rows (use biller_id + biller_transaction_id for unique key)
        groups[key].transactions.push({
          unique_key: (row.biller_id || '') + '_' + (row.biller_transaction_id || ''),
          biller_transaction_id: row.biller_transaction_id,
          insurance_name: row.insurance_name,
          posted_date: row.posted_date,
          received_amount: row.received_amount,
          total_balance: row.total_balance,
          status: row.status,
          comment: row.comment || null,
          updated_at: row.updated_at,
          import_id: row.import_id || null,
          latest_update_import_id: row.latest_update_import_id || null,
        });
      });

      // Calculate A/R aging for each group using max posted_date
      Object.keys(groups).forEach((key) => {
        const group = groups[key];
        if (group.latest_status === 'unbilled') {
          // For unbilled status, age from first day of month following service date to today
          if (group.service_date) {
            const serviceDate = moment(group.service_date);
            const firstDayOfNextMonth = serviceDate.clone().add(1, 'month').startOf('month');
            const today = moment().format('YYYY-MM-DD');
            group.ar_aging = this.calculateArAging(firstDayOfNextMonth.format('YYYY-MM-DD'), today);
          }
        } else if (group.posting_date) {
          if (group.latest_status === 'pending') {
            // For pending status, age from posting date to today
            group.ar_aging = this.calculateArAging(group.posting_date, moment().format('YYYY-MM-DD'));
          } else if (group.posted_dates.length > 0) {
            const maxPostedDate = group.posted_dates.reduce((max, date) => {
              if (!max || !moment(max).isValid()) return date;
              if (!date || !moment(date).isValid()) return max;
              return moment(date).isAfter(moment(max)) ? date : max;
            }, null);
            if (maxPostedDate) {
              group.ar_aging = this.calculateArAging(group.posting_date, maxPostedDate);
            }
          }
        }
        // Sort transactions by posted_date ascending
        group.transactions.sort((a, b) => {
          if (!a.posted_date) return -1;
          if (!b.posted_date) return 1;
          return moment(a.posted_date).valueOf() - moment(b.posted_date).valueOf();
        });
        // Clean up - don't need these in final output
        delete group.posted_dates;
        delete group.latest_posted_date;
        // Convert insurance names Set to display string
        group.insurance_name = [...group.insurance_names].sort().join(', ');
        delete group.insurance_names;
      });

      let result = Object.values(groups);

      // Filter out empty encounter IDs if toggled
      if (this.hideEmptyEncounterIds) {
        result = result.filter(g => g.encounter_id != null && g.encounter_id !== '');
      }

      // Filter by latest_status on grouped rows
      if (this.selectedStatuses.length > 0) {
        result = result.filter(g => this.selectedStatuses.includes(g.latest_status));
      }

      return result;
    },
    avgExpectedReimbursement() {
      const items = this.groupedTableData.filter(r => r.expected_reimbursement != null);
      if (!items.length) return '0.00';
      const sum = items.reduce((a, r) => a + parseFloat(r.expected_reimbursement), 0);
      return (sum / items.length).toFixed(2);
    },
    avgBilledAmount() {
      const items = this.groupedTableData.filter(r => r.billed_amount != null);
      if (!items.length) return '0.00';
      const sum = items.reduce((a, r) => a + parseFloat(r.billed_amount), 0);
      return (sum / items.length).toFixed(2);
    },
    avgReceivedAmount() {
      const items = this.groupedTableData.filter(r => r.total_received != null);
      if (!items.length) return '0.00';
      const sum = items.reduce((a, r) => a + parseFloat(r.total_received), 0);
      return (sum / items.length).toFixed(2);
    },
    avgArAging() {
      const validItems = this.groupedTableData.filter(r => r.ar_aging !== null);
      if (!validItems.length) return 'N/A';
      const sum = validItems.reduce((a, r) => a + r.ar_aging, 0);
      return (sum / validItems.length).toFixed(1);
    },
    totalOutstanding() {
      const items = this.groupedTableData.filter(r => r.expected_reimbursement != null && r.total_received != null);
      if (!items.length) return '$0.00';
      const sum = items.reduce((a, r) => a + (parseFloat(r.expected_reimbursement) - parseFloat(r.total_received)), 0);
      const abs = Math.abs(sum).toFixed(2);
      return sum < 0 ? '-$' + abs : '$' + abs;
    },
    totalExpectedReimbursement() {
      const items = this.groupedTableData.filter(r => r.expected_reimbursement != null);
      if (!items.length) return '0.00';
      const sum = items.reduce((a, r) => a + parseFloat(r.expected_reimbursement), 0);
      return sum.toFixed(2);
    },
    totalBilledAmount() {
      const items = this.groupedTableData.filter(r => r.billed_amount != null);
      if (!items.length) return '0.00';
      const sum = items.reduce((a, r) => a + parseFloat(r.billed_amount), 0);
      return sum.toFixed(2);
    },
    totalReceivedAmount() {
      const items = this.groupedTableData.filter(r => r.total_received != null);
      if (!items.length) return '0.00';
      const sum = items.reduce((a, r) => a + parseFloat(r.total_received), 0);
      return sum.toFixed(2);
    },
    chartData() {
      if (!this.arChartData || !this.arChartData.length) return null;

      const dateFilter = this.$options.filters.dateOrdinal;
      const labels = this.arChartData.map(item => dateFilter(item.date));

      const datasets = [
        {
          label: 'Avg Expected Reimbursement',
          data: this.arChartData.map(item => item.avg_expected_reimbursement),
          borderColor: '#42A5F5',
          backgroundColor: '#42A5F533',
          tension: 0.1,
          fill: false,
        },
        {
          label: 'Avg Billed Amount',
          data: this.arChartData.map(item => item.avg_billed_amount),
          borderColor: '#66BB6A',
          backgroundColor: '#66BB6A33',
          tension: 0.1,
          fill: false,
        },
        {
          label: 'Avg Received Amount',
          data: this.arChartData.map(item => item.avg_received_amount),
          borderColor: '#FFA726',
          backgroundColor: '#FFA72633',
          tension: 0.1,
          fill: false,
        },
      ];

      return { labels, datasets };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return '$' + value;
                },
              },
              scaleLabel: {
                display: true,
                labelString: 'Amount ($)'
              }
            }
          ],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Date'
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';
              if (label) {
                label += ': ';
              }
              label += '$' + tooltipItem.yLabel.toFixed(2);
              return label;
            }
          }
        },
      };
    },
  },
  methods: {
    dateFormatter(value) {
      return value ? moment(value).format('MMM D, YYYY') : '';
    },
    formatCurrency(value) {
      const num = parseFloat(value) || 0;
      return num.toFixed(2);
    },
    calculateArAging(postingDate, postedDate) {
      if (!postingDate || !postedDate) return null;
      const posting = moment(postingDate);
      const posted = moment(postedDate);
      return posted.diff(posting, 'days');
    },
    refreshData() {
      this.getData();
      if (this.activeTab === 1) {
        this.getChartData();
      } else {
        // Mark chart data as stale so it reloads when switching to chart tab
        this.chartDataStale = true;
      }
    },
    async getData() {
      if (!this.client) {
        return;
      }
      this.loading = true;
      this.tableData = [];

      try {
        const response = await this.$http.get('/api/rpm_ccm_dashboard/ar-report-data', {
          params: {
            token: this.token,
            client: this.client,
            date_from: this.dateFrom,
            date_to: this.dateTo,
          },
        });

        const rawData = response.data.data;

        // Map raw data - ar_aging is calculated in groupedTableData computed property
        this.tableData = rawData.map((row) => ({
          ...row,
          patient_name: [row.patient_first_name, row.patient_middle_name, row.patient_last_name].filter(Boolean).join(' '),
          client_name: row.client_name || '',
        }));
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || 'Error loading data';
        this.$awn.warning(message);
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async getChartData() {
      if (!this.client) {
        return;
      }
      this.chartLoading = true;

      try {
        var params = {
          token: this.token,
          client: this.client,
          time_period: this.chartTimePeriod,
          date_from: this.dateFrom,
          date_to: this.dateTo,
        };
        if (this.category) {
          params.category = this.category;
        }
        const response = await this.$http.get('/api/rpm_ccm_dashboard/ar-report-chart-data', {
          params: params,
        });

        this.arChartData = response.data.data;
        this.chartDataStale = false;
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || 'Error loading chart data';
        this.$awn.warning(message);
        console.error(error);
      } finally {
        this.chartLoading = false;
      }
    },
    csvLink() {
      const file = new File([this.arrayToCSV()], 'ar-report.csv', { type: 'text/csv' });
      return URL.createObjectURL(file);
    },
    arrayToCSV() {
      if (!this.tableDataFiltered.length) {
        return '';
      }
      const escapeField = (field) => '"' + ('' + field).replace(/"/g, '""').replace(/\n/g, '\\n') + '"';

      const csvHeaders = Object.keys(this.tableDataFiltered[0]).map(escapeField).join(',');
      const csvRows = this.tableDataFiltered.map((row) => Object.values(row).map(escapeField).join(',')).join('\n');

      return csvHeaders + '\n' + csvRows;
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 1 && this.chartDataStale) {
        this.getChartData();
      }
    },
    client() {
      this.getData();
      this.chartDataStale = true;
      if (this.activeTab === 1) {
        this.getChartData();
      }
    },
    dateFrom() {
      this.getData();
      this.chartDataStale = true;
      if (this.activeTab === 1) {
        this.getChartData();
      }
    },
    dateTo() {
      this.getData();
      this.chartDataStale = true;
      if (this.activeTab === 1) {
        this.getChartData();
      }
    },
    chartTimePeriod() {
      this.getChartData();
    },
    category() {
      this.chartDataStale = true;
      if (this.activeTab === 1) {
        this.getChartData();
      }
    },
  },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 450px;
}
</style>
